import {
  Box,
  Badge,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  SimpleGrid,
  Switch,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Business,
  BusinessSubscription,
  SubscriptionPlan,
  SubscriptionType,
} from 'services/@types';

const SUBSCRIPTION_TYPES: SubscriptionType[] = [
  'active',
  'trial',
  'cancelled',
  'expired',
  'pending',
  'trial_expired',
];

const statusColorMap: Record<string, string> = {
  active: 'green',
  trial: 'blue',
  cancelled: 'red',
  expired: 'gray',
  pending: 'orange',
  trial_expired: 'gray',
};

interface SubscriptionPanelProps {
  business: Business;
  allPlans: SubscriptionPlan[];
  saving: boolean;
  onUpdateSubscription: (updates: Record<string, any>) => void;
  onSwitchPlan: (planId: string) => void;
}

export default function SubscriptionPanel({
  business,
  allPlans,
  saving,
  onUpdateSubscription,
  onSwitchPlan,
}: SubscriptionPanelProps) {
  const { t } = useTranslation();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const sub = business.subscription || ({} as BusinessSubscription);

  const [newEndDate, setNewEndDate] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState('');

  const currentPlan =
    typeof sub.subscriptionPlanId === 'object'
      ? sub.subscriptionPlanId
      : (allPlans || []).find((p) => p.id === sub.subscriptionPlanId);

  const daysRemaining = sub.endDate
    ? Math.max(
        0,
        Math.ceil(
          (new Date(sub.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
        ),
      )
    : null;

  const formatDate = (date?: Date | string) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString();
  };

  return (
    <Card p="30px">
      <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
        {t('business_detail.subscription') || 'Subscription'}
      </Text>

      <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" mb="20px">
        <Box>
          <Text fontSize="sm" color="gray.500">
            Plan
          </Text>
          <Text fontSize="md" fontWeight="600" color={textColor}>
            {currentPlan?.name || '-'}
          </Text>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.500">
            Status
          </Text>
          <Badge
            colorScheme={statusColorMap[sub.type || ''] || 'gray'}
            fontSize="sm"
            px="10px"
            py="2px"
            borderRadius="8px"
          >
            {sub.type || 'N/A'}
          </Badge>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.500">
            Start Date
          </Text>
          <Text fontSize="md" color={textColor}>
            {formatDate(sub.startDate)}
          </Text>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.500">
            End Date
          </Text>
          <Text fontSize="md" color={textColor}>
            {formatDate(sub.endDate)}
            {daysRemaining !== null && (
              <Badge
                ml="8px"
                colorScheme={daysRemaining < 7 ? 'red' : 'green'}
                fontSize="xs"
              >
                {daysRemaining}d remaining
              </Badge>
            )}
          </Text>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.500">
            Is Paid
          </Text>
          <Badge
            colorScheme={sub.isPaid ? 'green' : 'red'}
            fontSize="sm"
            px="10px"
            py="2px"
            borderRadius="8px"
          >
            {sub.isPaid ? 'Yes' : 'No'}
          </Badge>
        </Box>
        <Box>
          <Text fontSize="sm" color="gray.500">
            Seat Count
          </Text>
          <Text fontSize="md" color={textColor}>
            {sub.seatCount ?? '-'}
          </Text>
        </Box>
      </SimpleGrid>

      <Text
        color={textColor}
        fontSize="lg"
        fontWeight="600"
        mb="15px"
        mt="10px"
      >
        Quick Actions
      </Text>
      <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
        <FormControl>
          <FormLabel fontSize="sm" color="gray.500">
            Change Status
          </FormLabel>
          <Select
            value={sub.type || ''}
            onChange={(e) =>
              onUpdateSubscription({ type: e.target.value })
            }
            size="sm"
            borderRadius="8px"
          >
            {SUBSCRIPTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Select>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.500">
            Toggle Paid
          </FormLabel>
          <Flex align="center" h="32px">
            <Switch
              isChecked={!!sub.isPaid}
              onChange={() =>
                onUpdateSubscription({ isPaid: !sub.isPaid })
              }
              colorScheme="brand"
            />
            <Text ml="10px" fontSize="sm" color={textColor}>
              {sub.isPaid ? 'Paid' : 'Unpaid'}
            </Text>
          </Flex>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.500">
            Extend End Date
          </FormLabel>
          <Flex gap="8px">
            <Input
              type="date"
              size="sm"
              borderRadius="8px"
              value={newEndDate}
              onChange={(e) => setNewEndDate(e.target.value)}
            />
            <Button
              size="sm"
              variant="darkBrand"
              borderRadius="8px"
              isLoading={saving}
              onClick={() => {
                if (newEndDate) {
                  onUpdateSubscription({
                    endDate: new Date(newEndDate).toISOString(),
                  });
                  setNewEndDate('');
                }
              }}
            >
              Set
            </Button>
          </Flex>
        </FormControl>

        <FormControl>
          <FormLabel fontSize="sm" color="gray.500">
            Switch Plan
          </FormLabel>
          <Flex gap="8px">
            <Select
              size="sm"
              borderRadius="8px"
              placeholder="Select plan"
              value={selectedPlanId}
              onChange={(e) => setSelectedPlanId(e.target.value)}
            >
              {(allPlans || []).map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} ({plan.period || '-'}) - {plan.price?.ils ?? 'N/A'} ILS
                </option>
              ))}
            </Select>
            <Button
              size="sm"
              variant="darkBrand"
              borderRadius="8px"
              isLoading={saving}
              onClick={() => {
                if (selectedPlanId) {
                  onSwitchPlan(selectedPlanId);
                  setSelectedPlanId('');
                }
              }}
            >
              Switch
            </Button>
          </Flex>
        </FormControl>
      </SimpleGrid>
    </Card>
  );
}
